import { ChangeEvent, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useHostingAccount } from '@newfold/huapi-js';

import useAccount from '~/hooks/useAccount';
import useFeature from '~/hooks/useFeature';
import { AddSiteFormDataTypes } from '~/scenes/AddSetupSiteFlows';
import CardHeader from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/CardHeader';
import { validateDomainPath } from '~/utils/domains/validations';

type DomainFormWithCTBPropOptions = {
  formData?: AddSiteFormDataTypes;
  setFormData?: (state: AddSiteFormDataTypes | any) => void;
  preventPath?: boolean;
  setPreventPath?: (state: boolean) => void;
  error?: boolean;
  setError?: (state: boolean) => void;
  value?: string;
  setValue?: (state: string) => void;
  disabled?: boolean;
};

const DomainFormWithCTB = ({
  formData = {},
  setFormData = () => {},
  preventPath = false,
  setPreventPath = () => {},
  error = false,
  setError = () => {},
  value = '',
  setValue = () => {},
  disabled = false,
  children = undefined,
}: PropsWithChildren<DomainFormWithCTBPropOptions>) => {
  const { t } = useTranslation('addSites');
  const { id: hostingId } = useAccount();

  const [optionSelected, setOptionSelected] = useState<number | boolean | null>(
    null,
  );

  const { data: hostingAccountData, isLoading: isPendingHostingAccount } =
    useHostingAccount(hostingId);
  const isHostingBasic = hostingAccountData?.data?.plan_key === 'basic';
  const [isHostingWPCloud] = useFeature('atomic');
  const isSiteGeneric = formData.subtype === 'generic';

  const pathNotAllowed = isHostingBasic || isSiteGeneric || isHostingWPCloud;

  // it applies to choose later option
  const autoGeneratedDomainBtn = () => {
    if (preventPath) setPreventPath(false);
    setValue(''); // clear url input if temp domain selected
    setFormData({
      ...formData,
      domain: '',
      path: '',
      siteUrl: '',
      autoGeneration: true,
    });
  };

  useEffect(() => {
    formData?.autoGeneration && setOptionSelected(1);
    // Need to comment out lint rule to ensure function only runs ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: it'll get implemented once purchase URL is available
  // it applies to purchase option
  // const purchaseDomainBtn = () => {
  //   setFormData({
  //     ...formData,
  //     domain: '',
  //     path: '',
  //     siteUrl: '',
  //     autoGeneration: true,
  //   });
  // };

  const connectOptions = [
    {
      id: 1,
      testId: 'temp-domain',
      title: t('tempDomain'),
      description: t('tempDomainPlaceholder'),
      autoGenerated: autoGeneratedDomainBtn,
      disabled: disabled,
    },
    // TODO: it'll get implemented once purchase URL is available
    // {
    //   id: 2,
    //   title: t('purchaseDomain'),
    //   description: t('purchaseDomainPlaceholder'),
    //   autoGenerated: purchaseDomainBtn,
    // },
  ];

  const handleDomainChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (optionSelected) setOptionSelected(false); // unselect temp domain card

    const siteUrl = e?.target?.value;

    let url = undefined as URL | undefined;
    const isValidURL = validateDomainPath(siteUrl);

    if (siteUrl.length >= 1 && !isValidURL) setError(true);

    if (isValidURL) {
      setError(false);
      // SiteUrl here can be with or without http protocol.
      // Prepend one if no protocol is found to easily extract hostname and pathname necessary for site creation
      url = /^https?:\/\//i.test(siteUrl)
        ? new URL(siteUrl)
        : new URL(`http://${siteUrl}`);
    }

    const pathname = url?.pathname?.slice(1)?.replace(/\/+$/, ''); // remove trailing slash
    if (pathNotAllowed) setPreventPath(!!pathname);

    setValue(siteUrl);
    setFormData({
      ...formData,
      domain: url?.hostname || siteUrl,
      path: pathname || '',
      autoGeneration: false,
      siteUrl: url?.href || '',
    });
  };

  const getHelperText = () => {
    if (preventPath) return t('pathNotAllowedMsg');
    if (error && value !== '') return t('errorMsg');
    return pathNotAllowed ? t('helperTextDomain') : t('helperTextURL');
  };

  return (
    <Stack spacing={2} data-testid="ctb-domain-connect-form">
      <CardHeader headerTitle={t('connectDomain')} />
      <Typography>{t('chooseDomain')}</Typography>
      <Stack spacing={4}>
        <Grid container>
          <Grid item xs={12} md={10}>
            <Stack spacing={2}>
              {isPendingHostingAccount ? (
                <Skeleton height="50px" />
              ) : (
                <TextField
                  variant="outlined"
                  fullWidth
                  id="site-url-input"
                  data-testid="site-url"
                  label={t('enterDomain')}
                  value={value}
                  disabled={disabled}
                  helperText={getHelperText()}
                  error={(error && value !== '') || preventPath}
                  onChange={handleDomainChange}
                />
              )}
              <Typography align="center">{t('or')}</Typography>
              {connectOptions.map((optionBtn) => (
                <Card
                  key={optionBtn.id}
                  variant={
                    optionBtn.id === optionSelected ? 'selected' : 'contained'
                  }
                  onClick={() => {
                    if (!optionBtn.disabled) {
                      optionBtn.autoGenerated();
                      setOptionSelected(optionBtn.id);
                    }
                  }}
                  data-testid={optionBtn.testId}
                  data-selected={optionBtn.id === optionSelected}
                >
                  <CardActionArea>
                    <CardContent sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Typography variant="h3">{optionBtn.title}</Typography>
                        <Typography variant="body2">
                          {optionBtn.description}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </Grid>
        </Grid>
        {children}
      </Stack>
    </Stack>
  );
};

export default DomainFormWithCTB;
